import { useCallback, useEffect, useRef, useState } from "react";
import MainLayout from "../../components/layout";
import DashboardGrid, { AppCard, SessionsMap } from "../../components/dashboard";
import {
  APP_DETAILS,
  APP_TO_SUBSCRIPTIONS_MAPPING,
  AppId,
  Subscriptions,
  SUBSCRIPTION_NAME_MAP,
} from "@dashboard/constants/appRegistry";
import LoadingSpinner from "../../components/modal";
import PWAInfoPopup from "../../components/modal/PWAInfoPopup";
import { useAuth, useSessionSocket } from "@workspace/ui/hooks";
import { useIsOnline } from "@dashboard/features/online";
import { authServices, profileServices, selectedAppStore } from "@workspace/shared";

export const Dashboard = () => {
  const { setSelectedAppState } = selectedAppStore.getState();
  const [appsBySubscription, setAppsBySubscription] = useState<Partial<Record<AppId, AppCard>>>({});
  const [currentSessions, setCurrentSessions] = useState<SessionsMap>({});
  const [loading, setLoading] = useState(true);
  const [showInstallMessage, setShowInstallMessage] = useState(false);
  const { isAuthenticated } = useAuth();
  const { isOnline } = useIsOnline();

  // Live session updates: a takeover/admin revoke emits `session:released`
  // (hard redirect, in-game parity); any register/release anywhere emits
  // `session:changed` → refetch the tile locks in real time, no reload.
  // fetchSessions is defined below — reach it through a ref.
  const fetchSessionsRef = useRef<(() => Promise<void>) | null>(null);
  useSessionSocket({
    onSessionsChanged: () => {
      void fetchSessionsRef.current?.();
    },
  });

  const isIosSafari = (): boolean => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    // iPadOS Safari masquerades as a desktop Mac in its user agent; a real
    // laptop Mac has no touch points, so this only matches actual iPads —
    // the install prompt must NOT show in laptop browsers.
    const isIpadMasqueradingAsMac =
      /macintosh|mac os x/.test(ua) && navigator.maxTouchPoints > 1;
    const isSafari =
      ua.includes("safari") &&
      !ua.includes("crios") &&
      !ua.includes("fxios") &&
      !ua.includes("chrome") &&
      !ua.includes("chromium") &&
      !ua.includes("edg") &&
      !ua.includes("firefox");
    return (isIOS || isIpadMasqueradingAsMac) && isSafari;
  };

  const isInStandaloneMode = (): boolean => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      ("standalone" in navigator && (navigator as any).standalone)
    );
  };

  const validateSessionAndGetApps = useCallback(async () => {
    try {
      let validSubscriptions: Subscriptions[] = [];

      if (isOnline) {
        setSelectedAppState({ key: "appKey", value: "Dashboard" });
        try {
          const { data: subscriptionResponse } = await profileServices.onGetSubscriptions();
          validSubscriptions = subscriptionResponse
            .map((sub: any) => {
              // Get the subscription type from API response. The API returns
              // null for Stripe products it has no mapping for (e.g. a plan id
              // env var not configured yet) — skip those, never crash on them.
              const apiSubType = typeof sub === "string" ? sub : sub?.type;
              if (!apiSubType) return null;
              // Normalize to enum value using the mapping; unknown types are for
              // other apps in the user's subscription set — silently skip.
              const normalized = SUBSCRIPTION_NAME_MAP[String(apiSubType).toLowerCase()];
              if (!normalized) return null;
              return normalized;
            })
            .filter((sub): sub is Subscriptions => sub !== null);

          // Cache subscriptions for offline mode
          localStorage.setItem("cachedSubscriptions", JSON.stringify(validSubscriptions));
        } catch (fetchError) {
          // Online but fetch failed (slow/timeout): fall back to cache
          console.warn("Failed to fetch subscriptions while online, using cache:", fetchError);
          const cached = localStorage.getItem("cachedSubscriptions");
          if (cached) {
            validSubscriptions = JSON.parse(cached);
          }
        }
      } else {
        // Offline: load from cache
        const cached = localStorage.getItem("cachedSubscriptions");
        if (cached) {
          validSubscriptions = JSON.parse(cached);
        }
      }

      // Build app cards based on user subscriptions
      const appCardMap: Partial<Record<AppId, AppCard>> = {};

      for (const [appId, subscriptionList] of Object.entries(APP_TO_SUBSCRIPTIONS_MAPPING)) {
        const matchedCount = validSubscriptions.reduce((count, subType) => {
          return subscriptionList.includes(subType) ? count + 1 : count;
        }, 0);

        if (matchedCount > 0) {
          const app = APP_DETAILS[appId as Exclude<AppId, AppId.Dashboard>];
          appCardMap[appId as AppId] = {
            apps: [app],
            count: matchedCount,
          };
        }
      }
      setAppsBySubscription(appCardMap);
    } catch (error: any) {
      console.log(error);
      console.error("Failed to validate session and get apps:", error);
      // Try to load from cache even on outer error
      const cached = localStorage.getItem("cachedSubscriptions");
      if (cached) {
        try {
          const validSubscriptions: Subscriptions[] = JSON.parse(cached);
          const appCardMap: Partial<Record<AppId, AppCard>> = {};
          for (const [appId, subscriptionList] of Object.entries(APP_TO_SUBSCRIPTIONS_MAPPING)) {
            const matchedCount = validSubscriptions.reduce((count, subType) => {
              return subscriptionList.includes(subType) ? count + 1 : count;
            }, 0);
            if (matchedCount > 0) {
              const app = APP_DETAILS[appId as Exclude<AppId, AppId.Dashboard>];
              appCardMap[appId as AppId] = {
                apps: [app],
                count: matchedCount,
              };
            }
          }
          setAppsBySubscription(appCardMap);
        } catch (cacheError) {
          console.warn("Failed to parse cached subscriptions:", cacheError);
        }
      }
      setLoading(false);
    }
    setLoading(false);
  }, [isOnline, setSelectedAppState]);

  // Per-app device sessions for the tile locks (mirrors the in-game
  // dashboard). Offline or on failure the map stays empty — no tiles lock,
  // matching in-game (the check simply can't run without a connection).
  const fetchSessions = useCallback(async () => {
    if (!isOnline) {
      setCurrentSessions({});
      return;
    }
    try {
      const { data } = await authServices.onGetCurrentSessions();
      setCurrentSessions((data ?? {}) as SessionsMap);
    } catch (error) {
      console.warn("Failed to fetch current sessions", error);
      setCurrentSessions({});
    }
  }, [isOnline]);
  fetchSessionsRef.current = fetchSessions;

  // In-game parity ("syncSessions"): arriving at the dashboard FIRST releases
  // every session row this device holds (POST /auth/logout under the
  // Dashboard app key — session rows only, auth tokens untouched), THEN
  // fetches the fresh map. This is what frees an app's slot when the coach
  // leaves it for the dashboard / switches apps — other devices unlock.
  const syncSessions = useCallback(async () => {
    if (!isOnline) {
      setCurrentSessions({});
      return;
    }
    try {
      await authServices.onReleaseDeviceSessions();
    } catch (error) {
      console.warn("Failed to release this device's sessions", error);
    }
    await fetchSessions();
  }, [isOnline, fetchSessions]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      validateSessionAndGetApps();
      void syncSessions();
    }
  }, [isAuthenticated, validateSessionAndGetApps, syncSessions]);

  // Coming back to the dashboard tab (from another window or the iPad home
  // screen) re-checks the locks. Fetch-only — releasing sessions belongs to
  // the mount path above, so a background tab regaining focus can't revoke
  // an app open in another tab of this same device.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => void fetchSessions();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void fetchSessions();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, fetchSessions]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowInstallMessage(false);
      return;
    }

    if (isIosSafari() && !isInStandaloneMode()) {
      setShowInstallMessage(true);
    } else {
      setShowInstallMessage(false);
    }
  }, [isAuthenticated]);

  const hasApps = Object.keys(appsBySubscription).length > 0;

  return (
    <>
      {showInstallMessage && <PWAInfoPopup />}
      <MainLayout showHeader={true}>
        <div className="flex w-full justify-center">
          {/* Wider symmetric insets (main px-4 + px-14 here = 72px per side):
            * the narrower 140px cards leave room to pull the grid in from the
            * edges, closer to the previous centered look. */}
          <div className="w-full bg-white px-14 pb-14 pt-10">
            <div className="mx-auto w-full text-center">
              <h1 className="font-roboto mt-4 text-lg font-bold leading-[140%] text-gray-900 sm:text-xl md:text-2xl lg:text-[28px]">
                Welcome to Modern Football
              </h1>
              {hasApps && (
                <p className="font-roboto mt-3 text-[16px] font-normal leading-[24px] text-[#374151]">
                  Choose your product to get started.
                </p>
              )}

              <div className="mt-9">
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <DashboardGrid
                    appsBySubscription={appsBySubscription}
                    currentSessions={currentSessions}
                    onSessionsRefresh={syncSessions}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};
